import {MAX_GETTY_IMAGES_PER_EMAIL, MAX_GETTY_VIDEOS_PER_EMAIL, MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import {getGettyMediaCount} from '../commands/editor';
import i18next from 'i18next';
import {INVALID_TARGET_ERROR, showWarningToast} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker-library';
import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

interface ComponentRemoveOptions {
  abort?: number;
}

interface ComponentDragEndData {
  target: Component;
  parent: Component;
  index: number;
}

/**
 * Listeners related to the Components
 * @param opt The grapesjs config object
 */
export default (opt: PrimaryConfigOptions): void => {
  const editor = opt.editor;

  editor.on('component:create', (component: Component) => {
    editor.runCommand(window.PMW.emails.commands.UPDATE_UNDO_REDO_STATES);

    syncGettyCountForComponent(editor, component);
  });

  editor.on('component:remove:before', (component: Component, removeFunc: any, opts: ComponentRemoveOptions) => {
    if (typeof component.get('deletable') !== 'undefined' && component.get('deletable') === false) {
      opts.abort = 1;
    }
  });

  document.addEventListener(window.PMW.emails.CUSTOM_EVENT_COMPONENT_STYLE_COPIED, () => {
    // eslint-disable-next-line no-restricted-globals
    $('.js-paste-styles').removeClass('-disabled');
  });

  editor.Commands.extend('select-comp', {
    /**
     * Returns element's data info
     * @param el The HTML element
     * @return Element position data
     * @private
     */
    getElementPos(el: HTMLElement): any {
      return this.canvas.getCanvasView().getElementPos(el);
    },
  });

  editor.on('component:mount', (component: Component) => {
    if (component.is(window.PMW.emails.components.container.PMW_CONTENT)) {
      editor.runCommand(window.PMW.emails.commands.ON_ALL_COMPONENTS_LOADED);
    }

    if (!canPremiumContentBeAdded(editor, component)) {
      const componentType = component.get('type') === window.PMW.emails.components.graphic.PMW_IMAGE ? 'image' : 'video';
      editor.runCommand(window.PMW.emails.commands.SHOW_GETTY_LIMIT_MESSAGE, {type: componentType});
      editor.getModel().skip(() => component.remove());
    } else if (!canVideoBeAdded(editor, component)) {
      editor.getModel().skip(() => component.remove());
      window.PMW.openMessageModal({
        title: i18next.t('pmwjs_video_limit'),
        text: i18next.t('pmwjs_video_limit_message', {num: window.PMW.emails.MAX_VIDEOS_ALLOWED}),
      });
    }
  });

  editor.on('component:add', (component: Component) => {
    editor.runCommand(window.PMW.emails.commands.UPDATE_TEXT_COMPONENT_LINK_COLOR, {component});
  });

  editor.on('component:update', (component: Component) => {
    editor.runCommand(window.PMW.emails.commands.UPDATE_UNDO_REDO_STATES);
    editor.runCommand(window.PMW.emails.commands.SANITIZE_COMPONENT_CONTENT, {component});
    editor.runCommand(window.PMW.emails.commands.TOGGLE_BUY_GETTY_DESIGN_MESSAGE);
    editor.runCommand(window.PMW.emails.commands.UPDATE_TEXT_COMPONENT_LINK_COLOR);
  });

  editor.on('component:remove', (component: Component) => {
    syncGettyCountForComponent(editor, component, true);
  });

  editor.on('component:selected', async (component: Component) => {
    if (component.is(window.PMW.emails.components.text.PMW_TEXT) || component.is(window.PMW.emails.components.text.PMW_BUTTON)) {
      await editor.runCommand(window.PMW.emails.commands.INIT_MERGE_TAGS);
      await editor.runCommand(window.PMW.emails.commands.INIT_TOOLBAR_AI_REWRITE_DROPDOWN);
    }

    editor.runCommand(window.PMW.emails.commands.CLOSE_SIDE_PANEL);
    editor.runCommand(window.PMW.emails.commands.UPDATE_STYLE_BUTTON_STATE, {});
  });

  editor.on('component:drag:end', ({target, parent, index}: ComponentDragEndData) => {
    editor.runCommand(window.PMW.emails.commands.MOVE_COMPONENT_ANCHOR, {component: target});
  });

  editor.on('log', (msg: string) => {
    if (msg === INVALID_TARGET_ERROR) {
      showWarningToast(window.i18next.t('pmwjs_invalid_target_error'));
    }
  });
};

function syncGettyCountForComponent(editor: Editor, component: Component, removed: boolean = false): void {
  const style = component.get('style');
  const hasBg = style && typeof style === 'object' ? style['background-image'] : undefined;
  const hasImg = component.get(window.PMW.emails.props.IS_MEDIA) as boolean;

  if (hasImg || hasBg) {
    if (component.get(window.PMW.emails.props.CONTENT_SOURCE) === MULTI_MEDIA_SOURCE_GETTY) {
      editor.runCommand(window.PMW.emails.commands.UPDATE_GETTY_COUNT, {
        type: editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_MEDIA_TYPE, {component: component}) as string,
        deltaCount: removed ? -1 : 1,
      });
    }
  }
}

function canPremiumContentBeAdded(editor: Editor, component: Component): boolean {
  let canAdd = true;
  const hasBg = component.getStyle()?.['background-image'];
  const hasPremiumContent = component.get(window.PMW.emails.props.CONTENT_SOURCE) === MULTI_MEDIA_SOURCE_GETTY;
  const isImg = component.get('src') && component.get('type') === window.PMW.emails.components.graphic.PMW_IMAGE;
  const isVideo = component.get('src') && component.get('type') === window.PMW.emails.components.graphic.PMW_VIDEO;
  const hasImg = hasBg || isImg;

  if (hasPremiumContent && hasImg && !isVideo) {
    const gettyCount = getGettyMediaCount(editor, 'image');
    canAdd = gettyCount <= MAX_GETTY_IMAGES_PER_EMAIL;
  }

  if (hasPremiumContent && isVideo) {
    const gettyCount = getGettyMediaCount(editor, 'video');
    canAdd = gettyCount <= MAX_GETTY_VIDEOS_PER_EMAIL;
  }

  return canAdd;
}

function canVideoBeAdded(editor: Editor, component: Component): boolean {
  const style = component.getStyle();
  const bg = style && typeof style === 'object' ? style['background-image'] : undefined;
  const hasBgVideo = bg && typeof bg === 'string' && /\.gif/ig.test(bg);
  const isVideo = component.is(window.PMW.emails.components.graphic.PMW_VIDEO);

  if (!hasBgVideo && !isVideo) {
    return true;
  }

  const currentVideoCount = editor.runCommand(window.PMW.emails.commands.GET_VIDEO_COUNT) as number;
  const allowedCount = window.PMW.emails.MAX_VIDEOS_ALLOWED - currentVideoCount;
  return allowedCount >= 0;
}